import type { z } from 'zod';

/**
 * Validation Utilities
 *
 * Helper functions for integrating Zod validation with TanStack Form.
 * These utilities bridge the gap between Zod's validation API and
 * TanStack Form's validator interface.
 */

/**
 * Validates a field value against a Zod schema
 * Returns error message string or undefined if valid
 *
 * @param schema - Zod schema to validate against
 * @param value - Value to validate
 * @returns Error message or undefined
 *
 * @example
 * ```tsx
 * <form.Field
 *   name="email"
 *   validators={{
 *     onBlur: ({ value }) => validateWithZod(loginSchema.shape.email, value),
 *   }}
 * >
 * ```
 */
export function validateWithZod<T>(
  schema: z.ZodType<T>,
  value: unknown
): string | undefined {
  const result = schema.safeParse(value);
  if (!result.success && result.error?.issues?.[0]) {
    return result.error.issues[0].message;
  }
  return undefined;
}

/**
 * Creates a TanStack Form validator function from a Zod schema
 * Useful for creating reusable validators
 *
 * @param schema - Zod schema to create validator from
 * @returns Validator function compatible with TanStack Form
 *
 * @example
 * ```tsx
 * const emailValidator = createZodValidator(loginSchema.shape.email);
 *
 * <form.Field
 *   name="email"
 *   validators={{ onBlur: emailValidator }}
 * >
 * ```
 */
export function createZodValidator<T>(schema: z.ZodType<T>) {
  return ({ value }: { value: unknown }): string | undefined => {
    return validateWithZod(schema, value);
  };
}

/**
 * Extracts error message from TanStack Form field errors array
 * Handles both string errors and error objects with message property
 *
 * @param errors - Array of errors from field.state.meta.errors
 * @returns First error message or undefined
 *
 * @example
 * ```tsx
 * error={field.state.meta.isTouched ? getFieldError(field.state.meta.errors) : undefined}
 * ```
 */
export function getFieldError(errors: unknown[]): string | undefined {
  if (errors.length === 0) return undefined;
  const error = errors[0];
  if (typeof error === 'string') return error;
  if (error && typeof error === 'object' && 'message' in error) {
    return (error as { message: string }).message;
  }
  return 'Invalid value';
}

/**
 * Validates an entire form data object against a Zod schema
 * Returns the validated data or throws validation errors
 *
 * @param schema - Zod schema to validate against
 * @param data - Form data to validate
 * @returns Validated and typed data
 * @throws ZodError if validation fails
 *
 * @example
 * ```tsx
 * onSubmit: async ({ value }) => {
 *   const validData = validateFormData(loginSchema, value);
 *   await login(validData.email, validData.password);
 * }
 * ```
 */
export function validateFormData<T>(
  schema: z.ZodType<T>,
  data: unknown
): T {
  return schema.parse(data);
}

/**
 * Safely validates form data, returning a result object
 * Does not throw - returns success/error state
 *
 * @param schema - Zod schema to validate against
 * @param data - Form data to validate
 * @returns Object with success boolean and either data or error
 *
 * @example
 * ```tsx
 * const result = safeValidateFormData(loginSchema, value);
 * if (result.success) {
 *   await login(result.data.email, result.data.password);
 * } else {
 *   setError(result.error.errors[0].message);
 * }
 * ```
 */
export function safeValidateFormData<T>(
  schema: z.ZodType<T>,
  data: unknown
): { success: true; data: T } | { success: false; error: z.ZodError } {
  const result = schema.safeParse(data);
  if (result.success) {
    return { success: true, data: result.data };
  }
  return { success: false, error: result.error };
}
